import { useEffect, useState } from "react";
import { Flame, TrendingUp, Calendar, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";
import { toast } from "sonner";

const WEEKDAY_LABEL = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MONTH_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                  "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function fmtDateLong(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${parseInt(d)} ${MONTH_PT[parseInt(m) - 1]} ${y}`;
}

/**
 * /admin/mapa-calor — booking heat-map over the last N months.
 * Uses CSS grid (7 columns = weekdays, rows = weeks) with cell
 * intensity proportional to the day's booking count vs window peak.
 * Hover shows count + revenue. Empty future weekdays under 5 highlight
 * as "candidatos a promo" so admin can decide where to push offers.
 */
export default function AdminHeatmap() {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/admin/heatmap?months=${months}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [months]);

  if (loading) return <div className="text-[#a0b2af]">A gerar mapa de calor…</div>;

  const days = data?.days || [];
  const summary = data?.summary || {};
  const peak = summary.peak || 1;
  const slow = summary.slow_upcoming_weekdays || [];

  return (
    <div className="space-y-8" data-testid="admin-heatmap">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3 flex items-center gap-2">
            <Flame size={12} /> Heat-map · Janela {data?.window?.start} → {data?.window?.end}
          </div>
          <h1 className="font-display text-5xl">Carga de limpezas.</h1>
          <p className="text-sm text-[#a0b2af] max-w-xl mt-3">
            Veja onde se concentra a procura ao longo do tempo. Quanto mais
            saturada a cor, mais limpezas marcadas nesse dia. Identifique
            picos a evitar e dias mortos onde aplicar promoções dinâmicas.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="heatmap-window-picker">
          {[1, 3, 6].map(n => (
            <button key={n} onClick={() => setMonths(n)}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                      months === n
                        ? "bg-[#d4af37] text-[#031c18] border-[#d4af37]"
                        : "border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37]"
                    }`}
                    data-testid={`window-${n}m`}>
              {n} {n === 1 ? "mês" : "meses"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Total reservas" value={summary.total || 0} accent="#d4af37"
             testId="hm-stat-total" />
        <KPI label="Concluídas" value={summary.completed || 0} accent="#10b981"
             testId="hm-stat-completed" />
        <KPI label="Receita realizada"
             value={`${(summary.revenue || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
             testId="hm-stat-revenue" />
        <KPI label="Média / dia" value={summary.avg_per_day || 0}
             accent="#f59e0b" testId="hm-stat-avg" hint={`Pico: ${peak}`} />
      </div>

      {/* Grid */}
      <section className="lux-card p-6" data-testid="heatmap-grid">
        <HeatGrid days={days} peak={peak} />
        <Legend peak={peak} />
      </section>

      {/* Weekday averages */}
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="lux-card p-6" data-testid="heatmap-weekdays">
          <div className="eyebrow mb-4 flex items-center gap-2">
            <TrendingUp size={12} /> Média por dia da semana
          </div>
          <div className="space-y-2">
            {(summary.weekday_avg || []).map(w => {
              const max = Math.max(...(summary.weekday_avg || []).map(x => x.avg), 1);
              const pct = (w.avg / max) * 100;
              return (
                <div key={w.weekday} className="flex items-center gap-3 text-sm">
                  <span className="w-10 text-[10px] uppercase tracking-widest text-[#a0b2af]">
                    {WEEKDAY_LABEL[w.weekday]}
                  </span>
                  <div className="flex-1 h-5 bg-[#031c18] border border-[#1a4640] rounded-sm overflow-hidden">
                    <div className="h-full bg-[#d4af37]/70 transition-all"
                         style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-16 text-right font-display text-[#d4af37]">{w.avg.toFixed(1)}</span>
                  <span className="w-10 text-right text-[10px] text-[#a0b2af]">pico {w.max}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="lux-card p-6" data-testid="heatmap-slow">
          <div className="eyebrow mb-4 flex items-center gap-2 text-[#f59e0b]">
            <AlertCircle size={12} /> Candidatos a promoção
          </div>
          <p className="text-xs text-[#a0b2af] mb-4">
            Dias úteis futuros sem qualquer reserva — alvo natural para
            descontos relâmpago, lembretes a hosts ou reforço de marketing.
          </p>
          {slow.length === 0 ? (
            <div className="text-sm text-[#a0b2af] italic">
              Sem dias mortos nos próximos {months * 30} dias — boa carga!
            </div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {slow.map(d => (
                <li key={d} className="flex items-center justify-between border-b border-[#1a4640]/40 py-1.5"
                    data-testid={`slow-${d}`}>
                  <span className="flex items-center gap-2">
                    <Calendar size={12} className="text-[#a0b2af]" />
                    {fmtDateLong(d)}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
                    {WEEKDAY_LABEL[new Date(d).getDay() === 0 ? 6 : new Date(d).getDay() - 1]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function KPI({ label, value, accent = "#d4af37", testId, hint }) {
  return (
    <div className="lux-card p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className="font-display text-3xl mt-1.5" style={{ color: accent }}>{value}</div>
      {hint && <div className="text-[10px] text-[#a0b2af] mt-1">{hint}</div>}
    </div>
  );
}

function HeatGrid({ days, peak }) {
  if (!days.length) {
    return <div className="text-sm text-[#a0b2af] italic text-center py-8">Sem dados na janela selecionada.</div>;
  }
  // Pad start so the grid begins on Monday
  const firstWeekday = days[0].weekday; // 0..6, 0 = Monday
  const padStart = Array.from({ length: firstWeekday }, () => null);
  const cells = [...padStart, ...days];
  const today = todayLocalIso();

  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <div
        className="grid gap-1"
        style={{
          gridTemplateColumns: `repeat(${Math.ceil(cells.length / 7)}, minmax(14px, 1fr))`,
          gridAutoFlow: "column",
          gridTemplateRows: "repeat(7, 14px)",
          minWidth: `${Math.ceil(cells.length / 7) * 16}px`,
        }}
      >
        {cells.map((d, i) => {
          if (!d) return <div key={`pad-${i}`} className="rounded-[2px]" />;
          const intensity = peak > 0 ? d.count / peak : 0;
          let bg = "#062c27";
          if (d.count > 0) {
            // Five-step ramp: dark green → bright gold
            const alpha = 0.18 + intensity * 0.82;
            bg = `rgba(212, 175, 55, ${alpha.toFixed(2)})`;
          }
          const isToday = d.date === today;
          return (
            <div key={d.date}
                 title={`${d.date} · ${d.count} reservas · ${d.revenue.toFixed(2)} €`}
                 data-testid={`hm-cell-${d.date}`}
                 className="rounded-[2px] cursor-pointer transition-transform hover:scale-150 hover:z-10 hover:relative"
                 style={{
                   background: bg,
                   border: isToday ? "1px solid #d4af37" : "1px solid transparent",
                 }} />
          );
        })}
      </div>
    </div>
  );
}

function Legend({ peak }) {
  const stops = [0, 0.25, 0.5, 0.75, 1].map(p => ({
    p, count: Math.round(p * peak),
  }));
  return (
    <div className="flex items-center justify-end gap-2 mt-4 text-[10px] uppercase tracking-widest text-[#a0b2af]">
      <span>Menos</span>
      {stops.map(s => (
        <div key={s.p}
             className="w-4 h-4 rounded-[2px]"
             style={{
               background: s.p === 0 ? "#062c27"
                 : `rgba(212,175,55,${(0.18 + s.p * 0.82).toFixed(2)})`,
               border: "1px solid #1a4640",
             }}
             title={`${s.count} reservas`} />
      ))}
      <span>Mais</span>
      <span className="ml-3 text-[#d4af37]">Pico: {peak}</span>
    </div>
  );
}
